import { Fragment, useMemo, useState } from "react";
import clsx from "clsx";
import { Card } from "@/components/ui/Card";
import { SelectInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { buildEmployeeCostContexts, calcForEmployee } from "@/lib/personnelCostCalc";
import {
  formatEuro,
  formatEuroPerHour,
  PERSONNEL_DEPARTMENT_OPTIONS,
  personnelDepartmentLabel,
  settingsForLogin,
  upsertPersonnelCostEmployeeSettings,
  type PersonnelCostCategory,
  type PersonnelCostDepartment,
  type PersonnelCostEmployeeSettings,
  type PersonnelCostEntry,
} from "@/lib/personnelCosts";
import { toast } from "@/lib/toast";
import type { EmployeeOption } from "@/pages/personal/personalkosten/PersonalkostenPage";
import { CostWaterfall } from "@/pages/personal/personalkosten/CostWaterfall";

interface EmployeeCostOverviewProps {
  employees: EmployeeOption[];
  categories: PersonnelCostCategory[];
  entries: PersonnelCostEntry[];
  settings: PersonnelCostEmployeeSettings[];
  onSelectEmployee: (login: string) => void;
  onSettingsSaved: (settings: PersonnelCostEmployeeSettings) => void;
}

interface OverviewRow {
  login: string;
  display: string;
  department: PersonnelCostDepartment | null;
  grossMonthly: number;
  totalMonthly: number;
  hourlyRate: number;
  targetHourlyRate: number | null;
  result: ReturnType<typeof calcForEmployee>;
}

export function EmployeeCostOverview({
  employees,
  categories,
  entries,
  settings,
  onSelectEmployee,
  onSettingsSaved,
}: EmployeeCostOverviewProps) {
  const [expandedLogin, setExpandedLogin] = useState<string | null>(null);
  const [savingDepartment, setSavingDepartment] = useState<string | null>(null);

  const employeeContexts = useMemo(
    () => buildEmployeeCostContexts(employees, settings),
    [employees, settings],
  );

  const rows = useMemo<OverviewRow[]>(() => {
    return employees.map((emp) => {
      const empSettings = settingsForLogin(settings, emp.login);
      const result = calcForEmployee({
        login: emp.login,
        settings: empSettings,
        categories,
        allEntries: entries,
        employees: employeeContexts,
      });
      return {
        login: emp.login,
        display: emp.display,
        department: empSettings.department,
        grossMonthly: result.grossMonthly,
        totalMonthly: result.totalMonthly,
        hourlyRate: result.hourlyRate,
        targetHourlyRate: empSettings.target_hourly_rate,
        result,
      };
    });
  }, [employees, categories, entries, settings, employeeContexts]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        grossMonthly: acc.grossMonthly + row.grossMonthly,
        totalMonthly: acc.totalMonthly + row.totalMonthly,
      }),
      { grossMonthly: 0, totalMonthly: 0 },
    );
  }, [rows]);

  const departmentTotals = useMemo(() => {
    const map = new Map<string, { count: number; totalMonthly: number }>();
    for (const row of rows) {
      const key = row.department ?? "unassigned";
      const current = map.get(key) ?? { count: 0, totalMonthly: 0 };
      map.set(key, {
        count: current.count + 1,
        totalMonthly: current.totalMonthly + row.totalMonthly,
      });
    }
    return map;
  }, [rows]);

  async function handleDepartmentChange(login: string, department: string) {
    const empSettings = settingsForLogin(settings, login);
    const nextDepartment = department ? (department as PersonnelCostDepartment) : null;
    setSavingDepartment(login);
    try {
      const saved = await upsertPersonnelCostEmployeeSettings(login, {
        gross_monthly: empSettings.gross_monthly,
        weekly_hours: empSettings.weekly_hours,
        vacation_days: empSettings.vacation_days,
        bridge_holidays_days: empSettings.bridge_holidays_days,
        target_hourly_rate: empSettings.target_hourly_rate,
        department: nextDepartment,
      });
      onSettingsSaved(saved);
      toast.success("Abteilung gespeichert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Abteilung konnte nicht gespeichert werden.");
    } finally {
      setSavingDepartment(null);
    }
  }

  if (employees.length === 0) {
    return (
      <Card title="Keine Mitarbeiter">
        <p className="text-sm text-label-secondary">
          Es wurden keine aktiven Mitarbeiter aus Hero geladen.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card
        title="Personalkosten-Übersicht"
        description="Kosten je Mitarbeiter mit Abteilungszuordnung. Klicken Sie auf eine Zeile für die Detailaufschlüsselung oder den Rechner."
      >
        <div className="overflow-x-auto">
          <table className="eh-table w-full min-w-[960px]">
            <thead>
              <tr>
                <th className="w-8" />
                <th className="text-left">Mitarbeiter</th>
                <th className="text-left">Abteilung</th>
                <th className="text-right">Brutto/Monat</th>
                <th className="text-right">Gesamtkosten/Monat</th>
                <th className="text-right">Stundenlohn</th>
                <th className="text-right">Ziel-Stundenlohn</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const expanded = expandedLogin === row.login;
                return (
                  <Fragment key={row.login}>
                    <tr
                      className={clsx(
                        "cursor-pointer transition hover:bg-fill-secondary",
                        expanded && "bg-fill-secondary/60",
                      )}
                      onClick={() => setExpandedLogin(expanded ? null : row.login)}
                    >
                      <td className="text-center text-label-tertiary">{expanded ? "▾" : "▸"}</td>
                      <td className="font-medium text-label">{row.display}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <SelectInput
                          value={row.department ?? ""}
                          onChange={(e) => void handleDepartmentChange(row.login, e.target.value)}
                          disabled={savingDepartment === row.login}
                          inputClassName="min-w-[180px]"
                        >
                          <option value="">Nicht zugeordnet</option>
                          {PERSONNEL_DEPARTMENT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </SelectInput>
                      </td>
                      <td className="text-right tabular-nums">{formatEuro(row.grossMonthly)}</td>
                      <td className="text-right tabular-nums">{formatEuro(row.totalMonthly)}</td>
                      <td className="text-right tabular-nums font-semibold text-accent">
                        {formatEuroPerHour(row.hourlyRate)}
                      </td>
                      <td className="text-right tabular-nums text-label-secondary">
                        {row.targetHourlyRate != null ? formatEuroPerHour(row.targetHourlyRate) : "–"}
                      </td>
                    </tr>
                    {expanded ? (
                      <tr className="bg-card-elevated">
                        <td colSpan={7} className="p-4">
                          <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
                            <div>
                              <p className="mb-3 text-sm font-medium text-label">
                                Kostenverteilung – {row.display}
                              </p>
                              <CostWaterfall
                                lines={row.result.lines}
                                totalMonthly={row.result.totalMonthly}
                                compact
                              />
                            </div>
                            <div className="flex items-start">
                              <button
                                type="button"
                                className="rounded-lg border border-separator px-3 py-2 text-sm text-accent transition hover:bg-fill-secondary"
                                onClick={() => onSelectEmployee(row.login)}
                              >
                                Im Rechner öffnen →
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-separator font-semibold">
                <td />
                <td>Gesamt ({rows.length} MA)</td>
                <td />
                <td className="text-right tabular-nums">{formatEuro(totals.grossMonthly)}</td>
                <td className="text-right tabular-nums">{formatEuro(totals.totalMonthly)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      <Card title="Kosten nach Abteilung" description="Summe der voll belasteten Monatskosten je Abteilung.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { key: "monteur", label: personnelDepartmentLabel("monteur") },
            { key: "technisches_buero", label: personnelDepartmentLabel("technisches_buero") },
            { key: "buchhaltung_personal", label: personnelDepartmentLabel("buchhaltung_personal") },
            { key: "unassigned", label: personnelDepartmentLabel(null) },
          ].map((dept) => {
            const data = departmentTotals.get(dept.key) ?? { count: 0, totalMonthly: 0 };
            return (
              <div
                key={dept.key}
                className="rounded-lg border border-separator bg-card-elevated p-4"
              >
                <p className="text-sm font-medium text-label">{dept.label}</p>
                <p className="mt-1 text-xs text-label-secondary">{data.count} Mitarbeiter</p>
                <p className="mt-2 text-lg font-semibold tabular-nums text-accent">
                  {formatEuro(data.totalMonthly)}
                </p>
                <p className="text-xs text-label-secondary">/ Monat</p>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
